import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertTriangle, X, Calendar, Clock, Building, DollarSign } from 'lucide-react';

interface ReservationDetails {
  selectedSlots: Array<{
    id: string;
    sala_nome: string;
    hora_inicio: string;
    hora_fim: string;
    valor: number;
    data: string;
  }>;
  totalValue: number;
  selectedDate: string;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  reservationDetails?: ReservationDetails;
  isLoading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  reservationDetails,
  isLoading = false
}) => {
  if (!isOpen) return null;

  const formatDisplayDate = (dateString: string) => {
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl border-0 animate-in zoom-in-95 duration-300">
          <CardHeader className="relative pb-4">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
              disabled={isLoading}
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <CardTitle className="text-lg font-semibold text-gray-900">
                {title}
              </CardTitle>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <p className="text-gray-600 leading-relaxed">
              {message}
            </p>

            {/* Detalhes da Reserva */}
            {reservationDetails && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Detalhes da Reserva
                </h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Data:</span>
                    <span className="font-medium capitalize">
                      {formatDisplayDate(reservationDetails.selectedDate)}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <span className="text-sm text-gray-600">Horários selecionados:</span>
                    <div className="space-y-2">
                      {reservationDetails.selectedSlots.map((slot, index) => (
                        <div key={slot.id} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-sm">{slot.sala_nome}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Clock className="w-3 h-3" />
                              {slot.hora_inicio} - {slot.hora_fim}
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {formatCurrency(slot.valor)}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-gray-900">Total:</span>
                    </div>
                    <span className="text-lg font-bold text-green-600">
                      {formatCurrency(reservationDetails.totalValue)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Aviso sobre pagamento */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-blue-800 font-medium">Pagamento com Saldo da Carteira</p>
                <p className="text-blue-700 mt-1">
                  O valor será debitado automaticamente do seu saldo disponível.
                </p>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={onConfirm}
                disabled={isLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processando...
                  </div>
                ) : (
                  'Confirmar Reserva'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ConfirmationModal; 